/**
 * Payroll Reports — on-screen dashboard plus period summary/detail exports and
 * per-employee P9 certificates.
 * Authority: HR_PAYROLL_VIEW / HR_PAYROLL_MANAGE
 *
 * Endpoints:
 *   GET /reports/periods/{id}/summary.xlsx|.pdf
 *   GET /reports/periods/{id}/detail.xlsx|.pdf
 *   GET /reports/employees/{id}/p9/{year}.pdf|.xlsx
 *   GET /payslips/period/{periodId} — powers the on-screen dashboard (summary totals,
 *       deduction-category breakdown, paginated employee table) by aggregating each
 *       payslip's line items client-side; no dedicated aggregate endpoint exists yet.
 */
import { useEffect, useMemo, useState } from "react";
import { FileText, Search } from "lucide-react";
import { leaveApi } from "../../leave/api";
import type { EmployeeOption } from "../../leave/types";
import { payrollApi, downloadBlob } from "../api";
import { useAsyncData, usePagination } from "../hooks";
import { monthLabel, type PageResponse, type Payslip } from "../types";
import {
  Btn, Card, CardHeader, Pagination, PayrollError, PayrollLoading,
  SearchInput, Table, TBody, TD, TH, THead, TR, fmt,
} from "../components/PayrollUI";

function empLabel(e: EmployeeOption) {
  return e.fullName || e.employeeName || e.name || e.employeeNumber;
}

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

const STATUTORY_REMIT_OPTIONS = [
  { value: "PAYE", label: "PAYE" },
  { value: "NSSF", label: "NSSF" },
  { value: "SHIF", label: "SHIF" },
  { value: "HOUSING_LEVY", label: "Affordable Housing Levy" },
];

// Four report tabs instead of a single long scrolling page + dropdown filter — each tab is a
// focused view of the same period's data, so switching reports is a click, not a scroll.
type ReportTab = "SUMMARY" | "DETAILED" | "DEDUCTIONS" | "BENEFITS";

const REPORT_TABS: { value: ReportTab; label: string }[] = [
  { value: "SUMMARY", label: "Summary" },
  { value: "DETAILED", label: "Detailed Report" },
  { value: "DEDUCTIONS", label: "Deductions Report" },
  { value: "BENEFITS", label: "Benefits Report" },
];

/** Per-employee amount for the Deductions/Benefits tabs — reads the already-computed
 *  Payslip field directly rather than re-summing line items, so it can never drift
 *  from what the calculation engine actually charged. */
function categoryAmount(p: Payslip, tab: ReportTab): number {
  switch (tab) {
    case "DEDUCTIONS": return p.otherDeductionsTotal ?? 0;
    case "BENEFITS": return p.otherBenefitsTotal ?? 0;
    default: return p.totalDeductions ?? 0;
  }
}

const TONE = {
  indigo:  { border: "border-t-indigo-400",   head: "bg-indigo-50/70 text-indigo-700",   badge: "bg-indigo-100 text-indigo-700",   row: "bg-indigo-50/30",   amount: "text-indigo-700" },
  amber:   { border: "border-t-amber-400",    head: "bg-amber-50/70 text-amber-700",     badge: "bg-amber-100 text-amber-700",     row: "bg-amber-50/30",    amount: "text-amber-700" },
  emerald: { border: "border-t-emerald-400",  head: "bg-emerald-50/70 text-emerald-700", badge: "bg-emerald-100 text-emerald-700", row: "bg-emerald-50/30",  amount: "text-emerald-700" },
} as const;

/** A colour-coded category breakdown table — one per tone (statutory/other/benefit),
 *  so the three report sections are visually distinct instead of three identical grey tables. */
function BreakdownCard({ tone, title, subtitle, rows, emptyText }: {
  tone: keyof typeof TONE; title: string; subtitle?: string; rows: [string, number][]; emptyText: string;
}) {
  const t = TONE[tone];
  return (
    <Card noPad className={`border-t-4 ${t.border}`}>
      <CardHeader title={title} subtitle={subtitle} />
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className={`border-b ${t.head}`}>
            <tr>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Category</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-sm text-slate-400">{emptyText}</td></tr>
            ) : rows.map(([label, amount], i) => (
              <tr key={label} className={`transition-colors hover:bg-slate-50/60 ${i % 2 === 1 ? t.row : ""}`}>
                <td className="px-4 py-3.5">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.badge}`}>{label}</span>
                </td>
                <td className={`px-4 py-3.5 text-right font-semibold ${t.amount}`}>{fmt(amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Slice a full in-memory array into the PageResponse shape the shared <Pagination> expects —
 *  payslipsForPeriod returns every payslip in one call, so pagination here is client-side. */
function paginate<T>(items: T[], page: number, size: number): PageResponse<T> {
  const totalElements = items.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  return {
    content: items.slice(page * size, page * size + size),
    page,
    size,
    totalElements,
    totalPages,
  };
}

export default function PayrollReportsPage() {
  const [periodBusy, setPeriodBusy] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | "">("");
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | "">("");
  const [activeTab, setActiveTab] = useState<ReportTab>("SUMMARY");

  const [empSearch, setEmpSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState<EmployeeOption | null>(null);
  const [p9Year, setP9Year] = useState(currentYear);
  const [p9Busy, setP9Busy] = useState<string | null>(null);
  const [p9Error, setP9Error] = useState<string | null>(null);

  // Remittance Reports — what Finance actually sends to NSSF/SHIF/a SACCO/etc: one deduction
  // category, across an accounting range, one column per period.
  const [remitCategory, setRemitCategory] = useState<string>("PAYE");
  const [remitFromYear, setRemitFromYear] = useState(currentYear);
  const [remitFromMonth, setRemitFromMonth] = useState(currentMonth);
  const [remitToYear, setRemitToYear] = useState(currentYear);
  const [remitToMonth, setRemitToMonth] = useState(currentMonth);
  const [remitBusy, setRemitBusy] = useState<string | null>(null);
  const [remitError, setRemitError] = useState<string | null>(null);

  // Employee Statement — one employee's earnings/deductions history across an accounting range.
  const [stmtFromYear, setStmtFromYear] = useState(currentYear);
  const [stmtFromMonth, setStmtFromMonth] = useState(1);
  const [stmtToYear, setStmtToYear] = useState(currentYear);
  const [stmtToMonth, setStmtToMonth] = useState(currentMonth);
  const [stmtBusy, setStmtBusy] = useState<string | null>(null);
  const [stmtError, setStmtError] = useState<string | null>(null);

  const periods = useAsyncData(() => payrollApi.listPeriods(0, 100), []);
  const employees = useAsyncData(() => leaveApi.employeeOptions(), []);
  const deductionCategories = useAsyncData(() => payrollApi.listDeductionCategories(), []);
  const { page, size, setPage } = usePagination(0, 10);

  // Statutory codes first, then every active custom deduction category (sacco, bank loan, salary
  // advance, ...) — one flat list so the Category selector covers everything Finance might need
  // to remit, without the caller needing to know which bucket a given deduction lives in.
  const remitCategoryOptions = useMemo(
    () => [
      ...STATUTORY_REMIT_OPTIONS,
      ...(deductionCategories.data ?? [])
        .filter((c) => c.active)
        .map((c) => ({ value: c.code, label: c.name })),
    ],
    [deductionCategories.data],
  );

  const approvedPeriods = (periods.data?.content ?? []).filter((p) => p.status === "APPROVED" || p.status === "PAID");

  // Distinct years present among approved/paid periods, newest first — lets HR/Finance
  // scan through history instead of hunting a flat list of every period ever run.
  const availableYears = useMemo(
    () => [...new Set(approvedPeriods.map((p) => p.periodYear))].sort((a, b) => b - a),
    [approvedPeriods],
  );

  const yearFilteredPeriods = selectedYear
    ? approvedPeriods.filter((p) => p.periodYear === selectedYear)
    : approvedPeriods;

  const selectedPeriod = approvedPeriods.find((p) => p.id === selectedPeriodId) ?? null;

  // Default to the most recently processed pay period as soon as periods load, so the dashboard
  // shows something useful immediately instead of an empty state waiting for a manual pick.
  useEffect(() => {
    if (selectedPeriodId || approvedPeriods.length === 0) return;
    const latest = [...approvedPeriods].sort((a, b) =>
      b.periodYear - a.periodYear || b.periodMonth - a.periodMonth,
    )[0];
    setSelectedYear(latest.periodYear);
    setSelectedPeriodId(latest.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedPeriods.length]);

  const dashboard = useAsyncData(
    () => (selectedPeriodId ? payrollApi.payslipsForPeriod(Number(selectedPeriodId)) : Promise.resolve<Payslip[]>([])),
    [selectedPeriodId],
  );

  const summary = useMemo(() => {
    const rows = dashboard.data ?? [];
    return rows.reduce(
      (acc, p) => ({
        employeeCount: acc.employeeCount + 1,
        grossPay: acc.grossPay + (p.grossPay ?? 0),
        totalDeductions: acc.totalDeductions + (p.totalDeductions ?? 0),
        statutoryDeductions: acc.statutoryDeductions + (p.statutoryDeductionsTotal ?? 0),
        otherDeductions: acc.otherDeductions + (p.otherDeductionsTotal ?? 0),
        netPay: acc.netPay + (p.netPay ?? 0),
      }),
      { employeeCount: 0, grossPay: 0, totalDeductions: 0, statutoryDeductions: 0, otherDeductions: 0, netPay: 0 },
    );
  }, [dashboard.data]);

  /** Every category total for the period — grouped by line label, split three ways:
   *  statutory (PAYE/NSSF/SHIF/Housing Levy), other (bank loan, sacco, etc — subtracted
   *  from net pay) and benefit (car allowance, airtime, etc — added to gross pay). */
  const categoryBreakdown = useMemo(() => {
    const statutory = new Map<string, number>();
    const other = new Map<string, number>();
    const benefit = new Map<string, number>();
    for (const p of dashboard.data ?? []) {
      for (const line of p.lines ?? []) {
        const bucket = line.lineType === "STATUTORY" ? statutory : line.lineType === "BENEFIT" ? benefit : other;
        bucket.set(line.label, (bucket.get(line.label) ?? 0) + line.amount);
      }
    }
    const toRows = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1]);
    return { statutory: toRows(statutory), other: toRows(other), benefit: toRows(benefit) };
  }, [dashboard.data]);

  const employeePage = paginate(dashboard.data ?? [], page, size);

  function selectPeriod(id: number | "") {
    setSelectedPeriodId(id);
    setPage(0);
  }

  async function downloadPeriodReport(kind: "summary" | "detail", format: "xlsx" | "pdf") {
    if (!selectedPeriodId) return;
    const key = `${kind}-${format}`;
    setPeriodBusy(key);
    try {
      const blob = kind === "summary"
        ? await payrollApi.periodSummaryReport(Number(selectedPeriodId), format)
        : await payrollApi.periodDetailReport(Number(selectedPeriodId), format);
      downloadBlob(blob, `payroll-${kind}-${selectedPeriod?.periodCode ?? selectedPeriodId}.${format}`);
    } finally {
      setPeriodBusy(null);
    }
  }

  function applyRemitPreset(preset: "MONTH" | "YEAR") {
    if (preset === "MONTH") {
      setRemitFromYear(currentYear); setRemitFromMonth(currentMonth);
      setRemitToYear(currentYear); setRemitToMonth(currentMonth);
    } else {
      setRemitFromYear(currentYear); setRemitFromMonth(1);
      setRemitToYear(currentYear); setRemitToMonth(12);
    }
  }

  async function downloadRemittance(format: "xlsx" | "pdf") {
    const key = `remit-${format}`;
    setRemitBusy(key);
    setRemitError(null);
    try {
      const blob = await payrollApi.remittanceReport(remitCategory, remitFromYear, remitFromMonth, remitToYear, remitToMonth, format);
      const label = (remitCategoryOptions.find((o) => o.value === remitCategory)?.label ?? remitCategory)
        .toLowerCase().replace(/[^a-z0-9]+/g, "-");
      downloadBlob(blob, `remittance-${label}-${remitFromYear}${String(remitFromMonth).padStart(2, "0")}-${remitToYear}${String(remitToMonth).padStart(2, "0")}.${format}`);
    } catch (e) {
      setRemitError(e instanceof Error ? e.message : "Could not generate remittance report");
    } finally {
      setRemitBusy(null);
    }
  }

  function applyStmtPreset(preset: "YEAR" | "LAST_3") {
    if (preset === "YEAR") {
      setStmtFromYear(currentYear); setStmtFromMonth(1);
      setStmtToYear(currentYear); setStmtToMonth(12);
    } else {
      const d = new Date(currentYear, currentMonth - 1 - 2, 1);
      setStmtFromYear(d.getFullYear()); setStmtFromMonth(d.getMonth() + 1);
      setStmtToYear(currentYear); setStmtToMonth(currentMonth);
    }
  }

  async function downloadStatement(format: "pdf" | "xlsx") {
    if (!selectedEmp) return;
    setStmtBusy(format);
    setStmtError(null);
    try {
      const blob = await payrollApi.employeeStatement(selectedEmp.id, stmtFromYear, stmtFromMonth, stmtToYear, stmtToMonth, format);
      downloadBlob(blob, `statement-${selectedEmp.employeeNumber}-${stmtFromYear}${String(stmtFromMonth).padStart(2, "0")}-${stmtToYear}${String(stmtToMonth).padStart(2, "0")}.${format}`);
    } catch (e) {
      setStmtError(e instanceof Error ? e.message : "Could not generate statement");
    } finally {
      setStmtBusy(null);
    }
  }

  async function downloadP9(format: "pdf" | "xlsx") {
    if (!selectedEmp) return;
    setP9Busy(format);
    setP9Error(null);
    try {
      const blob = await payrollApi.employeeP9(selectedEmp.id, p9Year, format);
      downloadBlob(blob, `p9-${selectedEmp.employeeNumber}-${p9Year}.${format}`);
    } catch (e) {
      setP9Error(e instanceof Error ? e.message : "Could not generate P9");
    } finally {
      setP9Busy(null);
    }
  }

  const filteredEmployees = (employees.data ?? []).filter((e) =>
    !empSearch || empLabel(e).toLowerCase().includes(empSearch.toLowerCase()) || e.employeeNumber.toLowerCase().includes(empSearch.toLowerCase()),
  );

  return (
    <div className="space-y-5">
      <Card noPad>
        <CardHeader title="Period Reports" />
        <div className="space-y-4 p-5">
          {periods.loading && <PayrollLoading />}
          {periods.error && <PayrollError message={periods.error} onRetry={periods.reload} />}
          {!periods.loading && !periods.error && (
            <>
              <div className="flex flex-wrap gap-4">
                <label className="block text-xs font-semibold text-slate-500">
                  Year
                  <select
                    className="mt-1 w-full min-w-[9rem] rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(e.target.value ? Number(e.target.value) : "");
                      selectPeriod("");
                    }}
                  >
                    <option value="">All years</option>
                    {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
                <label className="block flex-1 text-xs font-semibold text-slate-500">
                  Period (approved or paid only)
                  <select
                    className="mt-1 w-full max-w-sm rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    value={selectedPeriodId}
                    onChange={(e) => selectPeriod(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">Select a period…</option>
                    {yearFilteredPeriods.map((p) => (
                      <option key={p.id} value={p.id}>{monthLabel(p.periodMonth)} {p.periodYear} ({p.periodCode})</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Btn variant="secondary" disabled={!selectedPeriodId} loading={periodBusy === "summary-xlsx"} loadingText="Preparing…" onClick={() => downloadPeriodReport("summary", "xlsx")}>Summary (Excel)</Btn>
                <Btn variant="secondary" disabled={!selectedPeriodId} loading={periodBusy === "summary-pdf"} loadingText="Preparing…" onClick={() => downloadPeriodReport("summary", "pdf")}>Summary (PDF)</Btn>
                <Btn variant="secondary" disabled={!selectedPeriodId} loading={periodBusy === "detail-xlsx"} loadingText="Preparing…" onClick={() => downloadPeriodReport("detail", "xlsx")}>Detail (Excel)</Btn>
                <Btn variant="secondary" disabled={!selectedPeriodId} loading={periodBusy === "detail-pdf"} loadingText="Preparing…" onClick={() => downloadPeriodReport("detail", "pdf")}>Detail (PDF)</Btn>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card noPad className="border-t-4 border-t-violet-400">
        <CardHeader title="Remittance Reports" />
        <div className="space-y-4 p-5">
          <div className="flex flex-wrap gap-4">
            <label className="block text-xs font-semibold text-slate-500">
              Category
              <select
                className="mt-1 w-full min-w-[13rem] rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                value={remitCategory}
                onChange={(e) => setRemitCategory(e.target.value)}
              >
                {remitCategoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold text-slate-500">
              From
              <div className="mt-1 flex gap-1.5">
                <select className="rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-violet-400" value={remitFromMonth} onChange={(e) => setRemitFromMonth(Number(e.target.value))}>
                  {MONTHS.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                <input type="number" className="w-24 rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-violet-400" value={remitFromYear} onChange={(e) => setRemitFromYear(Number(e.target.value))} />
              </div>
            </label>
            <label className="block text-xs font-semibold text-slate-500">
              To
              <div className="mt-1 flex gap-1.5">
                <select className="rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-violet-400" value={remitToMonth} onChange={(e) => setRemitToMonth(Number(e.target.value))}>
                  {MONTHS.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                <input type="number" className="w-24 rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-violet-400" value={remitToYear} onChange={(e) => setRemitToYear(Number(e.target.value))} />
              </div>
            </label>
            <div className="flex items-end gap-1.5">
              <Btn size="sm" variant="secondary" onClick={() => applyRemitPreset("MONTH")}>This Month</Btn>
              <Btn size="sm" variant="secondary" onClick={() => applyRemitPreset("YEAR")}>This Year</Btn>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Btn variant="secondary" loading={remitBusy === "remit-xlsx"} loadingText="Preparing…" onClick={() => downloadRemittance("xlsx")}>Download (Excel)</Btn>
            <Btn variant="secondary" loading={remitBusy === "remit-pdf"} loadingText="Preparing…" onClick={() => downloadRemittance("pdf")}>Download (PDF)</Btn>
            {remitError && <p className="text-sm text-red-600">{remitError}</p>}
          </div>
        </div>
      </Card>

      {selectedPeriodId && (
        <>
          {dashboard.loading && <PayrollLoading label="Loading dashboard…" />}
          {dashboard.error && <PayrollError message={dashboard.error} onRetry={dashboard.reload} />}
          {!dashboard.loading && !dashboard.error && (
            <>
              {/* Report tabs — switch between report views with a click instead of scrolling
                  past every section on one long page. */}
              <div className="flex flex-wrap gap-1 rounded-2xl border bg-white p-1.5 shadow-sm">
                {REPORT_TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => { setActiveTab(t.value); setPage(0); }}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                      activeTab === t.value ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <Card noPad>
                <CardHeader title="Summary" subtitle={`${monthLabel(selectedPeriod?.periodMonth)} ${selectedPeriod?.periodYear ?? ""} — ${selectedPeriod?.periodCode ?? ""}`} />
                <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-3 lg:grid-cols-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Employees</p>
                    <p className="mt-1 text-lg font-bold text-slate-800">{summary.employeeCount}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Gross Pay</p>
                    <p className="mt-1 text-lg font-bold text-slate-800">{fmt(summary.grossPay)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Statutory Deductions</p>
                    <p className="mt-1 text-lg font-bold text-slate-800">{fmt(summary.statutoryDeductions)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Other Deductions</p>
                    <p className="mt-1 text-lg font-bold text-slate-800">{fmt(summary.otherDeductions)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Net Pay</p>
                    <p className="mt-1 text-lg font-bold text-emerald-700">{fmt(summary.netPay)}</p>
                  </div>
                </div>
              </Card>

              {activeTab === "SUMMARY" && (
                <BreakdownCard
                  tone="indigo"
                  title="Statutory Deduction Breakdown"
                  rows={categoryBreakdown.statutory}
                  emptyText="No statutory deductions."
                />
              )}

              {activeTab === "DEDUCTIONS" && (
                <BreakdownCard
                  tone="amber"
                  title="Other Deduction Breakdown"
                  rows={categoryBreakdown.other}
                  emptyText="No other deductions."
                />
              )}

              {activeTab === "BENEFITS" && (
                <BreakdownCard
                  tone="emerald"
                  title="Benefit Breakdown"
                  rows={categoryBreakdown.benefit}
                  emptyText="No benefits assigned."
                />
              )}

              {activeTab !== "SUMMARY" && (
                <Card noPad className="border-t-4 border-t-slate-300">
                  <CardHeader title="Employee Detail" />
                  {employeePage.content.length === 0 ? (
                    <div className="p-5 text-sm text-slate-500">No payslips found for this period.</div>
                  ) : (
                    <>
                      <Table>
                        <THead>
                          <TH>Employee</TH><TH>Department</TH><TH right>Gross Pay</TH>
                          <TH right>{activeTab === "DETAILED" ? "Deductions" : REPORT_TABS.find((t) => t.value === activeTab)?.label}</TH>
                          <TH right>Net Pay</TH>
                        </THead>
                        <TBody>
                          {employeePage.content.map((p, i) => (
                            <TR key={p.id} className={i % 2 === 1 ? "bg-slate-50/60" : ""}>
                              <TD>
                                <div className="font-semibold text-slate-800">{p.employeeName}</div>
                                <div className="font-mono text-xs text-slate-400">{p.employeeNumber}</div>
                              </TD>
                              <TD muted>{p.departmentName ?? "—"}</TD>
                              <TD right><span className="font-semibold text-blue-700">{fmt(p.grossPay)}</span></TD>
                              <TD right><span className="font-semibold text-amber-700">{fmt(categoryAmount(p, activeTab))}</span></TD>
                              <TD right><span className="font-semibold text-emerald-700">{fmt(p.netPay)}</span></TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                      <div className="p-3">
                        <Pagination page={employeePage} onPageChange={setPage} />
                      </div>
                    </>
                  )}
                </Card>
              )}
            </>
          )}
        </>
      )}

      <Card noPad className="border-t-4 border-t-blue-400">
        <CardHeader title="Employee Reports" />
        <div className="space-y-4 p-5">
          <SearchInput value={empSearch} onChange={setEmpSearch} placeholder="Search employee by name or number…" />
          {employees.loading && <PayrollLoading />}
          {employees.error && <PayrollError message={employees.error} onRetry={employees.reload} />}
          {!employees.loading && !employees.error && (
            <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-100">
              {filteredEmployees.slice(0, 50).map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedEmp(e)}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition hover:bg-slate-50 ${selectedEmp?.id === e.id ? "bg-indigo-50" : ""}`}
                >
                  <span className="font-medium text-slate-800">{empLabel(e)}</span>
                  <span className="font-mono text-xs text-slate-400">{e.employeeNumber}</span>
                </button>
              ))}
            </div>
          )}

          {selectedEmp && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">{empLabel(selectedEmp)} <span className="font-mono text-xs font-normal text-slate-400">{selectedEmp.employeeNumber}</span></p>

              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-indigo-700">P9 Tax Certificate</p>
                <p className="mb-3 text-xs text-slate-500">Covers Basic Salary, Gross Pay, NSSF, Taxable Pay, Tax Charged, Personal Relief, PAYE — does not cover non-cash benefits, mortgage relief, or other retirement schemes; add those manually to a filing if applicable.</p>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                    Year
                    <input type="number" className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" value={p9Year} onChange={(e) => setP9Year(Number(e.target.value))} />
                  </label>
                  <Btn icon={FileText} loading={p9Busy === "pdf"} loadingText="Generating…" onClick={() => downloadP9("pdf")}>P9 (PDF)</Btn>
                  <Btn icon={Search} loading={p9Busy === "xlsx"} loadingText="Generating…" onClick={() => downloadP9("xlsx")}>P9 (Excel)</Btn>
                  {p9Error && <p className="text-sm text-red-600">{p9Error}</p>}
                </div>
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-blue-700">Payroll Statement</p>
                
                <div className="flex flex-wrap items-end gap-3">
                  <label className="block text-xs font-semibold text-slate-500">
                    From
                    <div className="mt-1 flex gap-1.5">
                      <select className="rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-blue-400" value={stmtFromMonth} onChange={(e) => setStmtFromMonth(Number(e.target.value))}>
                        {MONTHS.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                      </select>
                      <input type="number" className="w-24 rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-blue-400" value={stmtFromYear} onChange={(e) => setStmtFromYear(Number(e.target.value))} />
                    </div>
                  </label>
                  <label className="block text-xs font-semibold text-slate-500">
                    To
                    <div className="mt-1 flex gap-1.5">
                      <select className="rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-blue-400" value={stmtToMonth} onChange={(e) => setStmtToMonth(Number(e.target.value))}>
                        {MONTHS.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                      </select>
                      <input type="number" className="w-24 rounded-xl border border-slate-200 px-2 py-2 text-sm outline-none focus:border-blue-400" value={stmtToYear} onChange={(e) => setStmtToYear(Number(e.target.value))} />
                    </div>
                  </label>
                  <div className="flex gap-1.5">
                    <Btn size="sm" variant="secondary" onClick={() => applyStmtPreset("YEAR")}>This Year</Btn>
                    <Btn size="sm" variant="secondary" onClick={() => applyStmtPreset("LAST_3")}>Last 3 Months</Btn>
                  </div>
                  <Btn icon={FileText} loading={stmtBusy === "pdf"} loadingText="Generating…" onClick={() => downloadStatement("pdf")}>Statement (PDF)</Btn>
                  <Btn icon={Search} loading={stmtBusy === "xlsx"} loadingText="Generating…" onClick={() => downloadStatement("xlsx")}>Statement (Excel)</Btn>
                  {stmtError && <p className="text-sm text-red-600">{stmtError}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
